import { Trans, useLingui } from '@lingui/react/macro';
import { Button, toast } from '@utima/ui';
import { Ban, Bell, BellRing } from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Card } from '@/components/card/Card';
import { StripeCustomerIdField } from '@/features/organizations/components/StripeCustomerIdField';
import { trpc } from '@/services/trpc';

import { FieldLabelWithHint } from './FieldLabelWithHint';
import { inputClassName } from './planManagementShared';

type SuperAdminControlsCardProps = {
  organizationId: string;
  /** Stripe customer id field (SuperAdmin can link/unlink). */
  canLinkStripe: boolean;
  stripeCustomerId: string | null;
  onStripeUpdate: () => void;
  /** Net payment terms + DUZP for the next assign (SuperAdmin only). */
  daysUntilDue: number;
  onDaysUntilDueChange: (value: number) => void;
  duzpDate: string;
  onDuzpDateChange: (value: string) => void;
  /** Dunning / force cancel. */
  hasOpenOverdueInvoice: boolean;
  canForceCancel: boolean;
  onForceCancel: () => void;
  isCancelling: boolean;
  disabled?: boolean;
};

/**
 * SuperAdmin-only controls consolidated into one section: the Stripe customer
 * link, the invoice defaults (days until due + DUZP) applied by the next plan
 * assign, and the dunning lifecycle + force-cancel action. Partners never see
 * this card — their assigns use the default terms (14 days, no DUZP override).
 */
export function SuperAdminControlsCard({
  organizationId,
  canLinkStripe,
  stripeCustomerId,
  onStripeUpdate,
  daysUntilDue,
  onDaysUntilDueChange,
  duzpDate,
  onDuzpDateChange,
  hasOpenOverdueInvoice,
  canForceCancel,
  onForceCancel,
  isCancelling,
  disabled,
}: SuperAdminControlsCardProps) {
  const { t } = useLingui();
  const utils = trpc.useUtils();
  const reconcile = trpc.subscriptions.reconcileFromStripe.useMutation({
    onSuccess: async () => {
      await utils.subscriptions.invalidate();
      onStripeUpdate();
      toast.success(t`Subscription synced from Stripe.`);
    },
    onError: error => toast.error(error.message),
  });

  return (
    <Card title={t`SuperAdmin controls`} className='mb-6'>
      <div className='space-y-6'>
        {canLinkStripe && (
          <section className='space-y-2'>
            <h3 className='text-sm font-medium'>
              <Trans>Stripe customer</Trans>
            </h3>
            <StripeCustomerIdField
              organizationId={organizationId}
              currentValue={stripeCustomerId}
              onUpdate={onStripeUpdate}
            />
            <Button
              variant='secondary'
              size='sm'
              disabled={!stripeCustomerId || disabled || reconcile.isPending}
              loading={reconcile.isPending}
              onClick={() => reconcile.mutate({ organizationId })}
            >
              <Trans>Sync subscription from Stripe</Trans>
            </Button>
            <p className='text-muted-foreground text-xs'>
              <Trans>
                Refreshes the plan and add-ons. Preserves usage and balance. No
                invoices or emails are sent.
              </Trans>
            </p>
          </section>
        )}

        <section className='space-y-2'>
          <h3 className='text-sm font-medium'>
            <Trans>Invoice defaults</Trans>
          </h3>
          <p className='text-muted-foreground text-xs'>
            <Trans>
              Applied to the next plan assign and to void &amp; reissue.
            </Trans>
          </p>
          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
            <div className='space-y-2'>
              <FieldLabelWithHint
                htmlFor='days-until-due'
                label={<Trans>Days until due</Trans>}
                hint={
                  <Trans>
                    Net payment terms on the first invoice. Default is 14 days.
                  </Trans>
                }
              />
              <input
                id='days-until-due'
                type='number'
                min={1}
                max={60}
                value={daysUntilDue}
                onChange={event =>
                  onDaysUntilDueChange(
                    Number.parseInt(event.target.value || '14', 10),
                  )
                }
                className={inputClassName}
                disabled={disabled}
              />
            </div>
            <div className='space-y-2'>
              <FieldLabelWithHint
                htmlFor='duzp-date'
                label={<Trans>DUZP date</Trans>}
                hint={
                  <Trans>
                    Date of taxable supply for Czech accounting. Optional; leave
                    blank to use invoice date.
                  </Trans>
                }
              />
              <input
                id='duzp-date'
                type='date'
                value={duzpDate}
                onChange={event => onDuzpDateChange(event.target.value)}
                className={inputClassName}
                disabled={disabled}
              />
            </div>
          </div>
        </section>

        <section className='space-y-4'>
          <h3 className='text-sm font-medium'>
            <Trans>Dunning &amp; actions</Trans>
          </h3>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-muted-foreground inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium'>
              <Bell className='size-3.5' />
              <Trans>+1 day reminder</Trans>
            </span>
            <span className='text-muted-foreground inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium'>
              <BellRing className='size-3.5' />
              <Trans>+7 day escalation</Trans>
            </span>
            <span className='inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700 dark:bg-red-900/40 dark:text-red-300'>
              <Ban className='size-3.5' />
              <Trans>+14 day cancel</Trans>
            </span>
          </div>

          <p className='text-muted-foreground text-xs'>
            <Trans>
              Reminders and the +14-day cancellation run automatically via the
              scheduled dunning job. No chat suspension.
            </Trans>
          </p>

          {hasOpenOverdueInvoice && (
            <Alert
              variant='warning'
              title={<Trans>Overdue invoice detected</Trans>}
              description={
                <Trans>
                  Dunning reminders and the +14-day cancellation run
                  automatically via the scheduled job.
                </Trans>
              }
            />
          )}

          <Button
            variant='ghost'
            size='sm'
            className='self-start text-red-600 hover:bg-red-50 hover:text-red-700'
            disabled={!canForceCancel}
            loading={isCancelling}
            onClick={onForceCancel}
          >
            <Ban className='mr-1.5 size-4' />
            <Trans>Force cancel subscription</Trans>
          </Button>
        </section>
      </div>
    </Card>
  );
}
